<template>
  <div
    ref="productCardEl"
    class="catalog-product-card"
    :class="{ 'catalog-product-card_not-in-stock': isNotInStock }"
  >
    <div
      v-if="image"
      class="catalog-product-card__image-wrapper"
    >
      <img
        :src="getUploadUrl(image.attributes.url)"
        :height="height"
        alt="product image"
        class="catalog-product-card__image"
      />
    </div>

    <div class="catalog-product-card__info">
      <div class="catalog-product-card__name">
        {{ product.attributes.title }}
      </div>

      <div
        v-if="product.in_stock !== undefined"
        class="catalog-product-card__in-stock"
      >
        {{ stockText }}
      </div>

      <div
        v-if="product.attributes.price"
        class="catalog-product-card__price"
      >
        {{ product.attributes.price }} &#8381;
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, defineProps, ref } from 'vue';
  import { getUploadUrl } from '@/helpers/urls';
  import { Product } from '@/services/product/types';
  import { isVideoMedia } from '@/services/product/helpers';

  const props = defineProps<{
    product: Product;
  }>();

  const productCardEl = ref<HTMLElement | null>(null);

  const image = computed(() => {
    const defaultMedia = props.product.attributes.medias.find(media => media.is_main);

    if (!defaultMedia || isVideoMedia(defaultMedia)) {
      return props.product.attributes.medias[0]?.media.data || null;
    }

    return defaultMedia.media.data;
  });

  const height = computed(() => {
    if (!image.value || !productCardEl.value) {
      return 0;
    }

    const cardWidth = productCardEl.value.clientWidth;
    const imageRatio = (image.value.attributes.height || 0) / (image.value.attributes.width || 0);

    return Math.round(cardWidth * imageRatio);
  });

  const isNotInStock = computed(() => props.product.in_stock === 0);

  const stockText = computed(() => {
    if (isNotInStock.value) {
      return 'Нет в наличии';
    }

    return `${props.product.in_stock} шт.`;
  });
</script>

<style>
  .catalog-product-card {
    background: #f3f3f3;
    padding: 18px 0 12px;
    border-radius: 8px;
    /* margin-bottom: 24px; */
    cursor: pointer;
    transition: background 0.2s ease;
    color: #000;

    &:hover {
      background: #e8e8e8;
    }

    &__image-wrapper {
      width: 100%;
      display: flex;
      justify-content: center;
      margin-bottom: 24px;

      img {
        max-width: 100%;
      }
    }

    &__info {
      padding: 0 16px;
    }

    &__name {
      font-size: 15px;
      color: #000;
      font-weight: 500;
      word-break: break-word;
    }

    &__in-stock {
      font-size: 14px;
      color: #000;
      font-weight: 500;
      margin-top: 8px;
    }

    &__price {
      font-size: 21px;
      color: #fff;
      font-weight: 600;
      margin-top: 8px;
      background: #86ca2f;
      display: inline-block;
      padding: 2px 4px;
    }

    &_not-in-stock {
      .catalog-product-card__in-stock {
        opacity: 0.4;
      }

      .catalog-product-card__price {
        opacity: 0.4;
      }
    }
  }

  @media (--mobile) {
    .catalog-product-card {
      &__price {
        margin-top: 4px;
      }
    }
  }
</style>
